import { useCallback } from "react";

import InlineQuestionResponse from "../../../types/InlineQuestionResponse";
import type { QualificationAttribute } from "../../../types/JobAttribute";
import YellowLightCategoryType from "../../../types/YellowLightCategoryType";
import type { UpdateJobSeekerAttributesMutation } from "./__generated__/useUpdateJobSeekerAttributeWithSentiment.gqlgenerated";
import { useUpdateJobSeekerAttributeWithSentiment } from "./useUpdateJobSeekerAttributeWithSentiment";

/**
 * Extracts the jssdId from the GraphQL mutation response.
 * Returns the id if found, undefined otherwise.
 */
function getJssdIdFromResponse(
  response: UpdateJobSeekerAttributesMutation,
  attribute: QualificationAttribute,
): string | undefined {
  let matchedAttr;

  switch (attribute.type) {
    case YellowLightCategoryType.EDUCATION:
      matchedAttr = response.addJobSeekerProfileStructuredDataAttributes?.educations?.find(
        (responseAttr) =>
          responseAttr.degreeTaxonomyConcept?.suid === attribute.suid ||
          responseAttr.fieldTaxonomyConcept?.suid === attribute.suid,
      );
      break;
    case YellowLightCategoryType.CERTIFICATIONS:
      matchedAttr = response.addJobSeekerProfileStructuredDataAttributes?.certifications?.find(
        (responseAttr) => responseAttr.titleTaxonomyConcept?.suid === attribute.suid,
      );
      break;
    case YellowLightCategoryType.LICENSE:
      matchedAttr = response.addJobSeekerProfileStructuredDataAttributes?.licenses?.find(
        (responseAttr) => responseAttr.titleTaxonomyConcept?.suid === attribute.suid,
      );
      break;
    case YellowLightCategoryType.LANGUAGES:
      matchedAttr = response.addJobSeekerProfileStructuredDataAttributes?.languages?.find(
        (responseAttr) => responseAttr.languageTaxonomyConcept?.suid === attribute.suid,
      );
      break;
    case YellowLightCategoryType.SKILLS:
      matchedAttr = response.addJobSeekerProfileStructuredDataAttributes?.skills?.find(
        (responseAttr) => responseAttr.skillTaxonomyConcept?.suid === attribute.suid,
      );
      break;
  }

  return matchedAttr?.id ?? undefined;
}

export function useSendQualificationResponse() {
  const updateJobSeekerAttributeWithSentiment = useUpdateJobSeekerAttributeWithSentiment();
  return useCallback(
    async ({
      attribute,
      questionResponse,
      onQualificationSubmissionsSuccess,
      onQualificationSubmissionsFailure,
    }: {
      attribute: QualificationAttribute;
      questionResponse: InlineQuestionResponse;
      onQualificationSubmissionsSuccess: (
        qualification: QualificationAttribute,
        submissionSentiment: InlineQuestionResponse,
        jssdId?: string,
      ) => void;
      onQualificationSubmissionsFailure?: (error?: any) => void;
    }) => {
      let updateSucceeded = false;
      let jssdId: string | undefined;
      const sentiment = questionResponse === InlineQuestionResponse.YES;

      try {
        const { data: response } = await updateJobSeekerAttributeWithSentiment({
          attribute,
          sentiment,
        });

        if (!response || !response.addJobSeekerProfileStructuredDataAttributes) {
          throw new Error("Null response received from JSSD");
        }

        // Extract jssdId from response instead of mutating the attribute
        jssdId = getJssdIdFromResponse(response, attribute);

        updateSucceeded = true;
      } catch (err) {
        onQualificationSubmissionsFailure?.(err);
      }

      if (!updateSucceeded) {
        return false;
      }

      // Pass jssdId to the callback so it can update state immutably
      onQualificationSubmissionsSuccess(attribute, questionResponse, jssdId);
      return true;
    },
    [updateJobSeekerAttributeWithSentiment],
  );
}
